import React, { useState, useEffect } from 'react';
import { obtenerTituloElemento } from '../utils/gruposSubgruposProcessor';
import MENU_CONFIG, { shouldFilterGroupByName } from '../config/config-menu';
import './MenuGruposSubgrupos.css';
import evaluateMenuRelevant from '../utils/evaluateMenuRelevant';

// Helper: describir la expresión relevant en texto para debug
const getDisplayValue = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    if (v.type === 'literal') return String(v.value);
    if (v.type === 'field') return v.name || '[field]';
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};

const describeRelevantNode = (node) => {
  if (!node || typeof node !== 'object') return '';
  const type = node.type;
  if (type === 'comparison') {
    const field = node.field || '[field]';
    const op = node.operator || '=';
    const val = getDisplayValue(node.value);
    return `${field} ${op} ${val}`;
  }
  if (type === 'function') {
    const name = (node.name || '').toLowerCase();
    if (name === 'selected') {
      const field = node.field || (Array.isArray(node.args) && node.args[0] ? getDisplayValue(node.args[0]) : '[field]');
      const value = Object.prototype.hasOwnProperty.call(node, 'value') ? getDisplayValue(node.value) : (Array.isArray(node.args) && node.args[1] ? getDisplayValue(node.args[1]) : '');
      return `selected(${field}, ${value})`;
    }
    return `${name}(...)`;
  }
  if (type === 'logical') {
    const op = (node.op || 'and').toUpperCase();
    const items = Array.isArray(node.items) ? node.items : (Array.isArray(node.expressions) ? node.expressions : []);
    const parts = items.map(describeRelevantNode).filter(Boolean);
    return parts.join(` ${op} `);
  }
  try { return JSON.stringify(node); } catch { return String(node); }
};

const describeRelevantExpression = (expr) => {
  if (!expr) return '';
  if (Array.isArray(expr)) {
    const parts = expr.map(describeRelevantNode).filter(Boolean);
    return parts.join(' AND ');
  }
  return describeRelevantNode(expr);
};

// Helpers para debug de valores actuales/esperados
const extractFieldNamesFromNode = (node) => {
  if (!node || typeof node !== 'object') return [];
  if (node.type === 'comparison') return [node.field].filter(Boolean);
  if (node.type === 'function') {
    if (typeof node.field === 'string') return [node.field];
    if (Array.isArray(node.args) && node.args[0] && node.args[0].type === 'field') return [node.args[0].name];
    return [];
  }
  if (node.type === 'logical') {
    const items = Array.isArray(node.items) ? node.items : (Array.isArray(node.expressions) ? node.expressions : []);
    return items.flatMap(extractFieldNamesFromNode);
  }
  return [];
};

const extractExpectedValuesFromNode = (node) => {
  if (!node || typeof node !== 'object') return [];
  if (node.type === 'comparison') {
    const v = node.value;
    if (v && typeof v === 'object' && v.type === 'literal') return [String(v.value)];
    return [getDisplayValue(v)];
  }
  if (node.type === 'function') {
    const name = (node.name || '').toLowerCase();
    if (name === 'selected') {
      if (Object.prototype.hasOwnProperty.call(node, 'value')) return [getDisplayValue(node.value)];
      if (Array.isArray(node.args) && node.args[1]) return [getDisplayValue(node.args[1])];
    }
    return [];
  }
  if (node.type === 'logical') {
    const items = Array.isArray(node.items) ? node.items : (Array.isArray(node.expressions) ? node.expressions : []);
    return items.flatMap(extractExpectedValuesFromNode);
  }
  return [];
};

const buildDebugValuesTooltip = (expr, datos) => {
  try {
    const fields = Array.isArray(expr) ? expr.flatMap(extractFieldNamesFromNode) : extractFieldNamesFromNode(expr);
    const expected = Array.isArray(expr) ? expr.flatMap(extractExpectedValuesFromNode) : extractExpectedValuesFromNode(expr);
    const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
    const fieldsUniq = uniq(fields);
    const expectedUniq = uniq(expected);
    const lines = [];
    const pickFieldVal = (raw) => {
       if (raw === null || raw === undefined) return raw;
       if (Array.isArray(raw)) return raw;
       if (typeof raw === 'object') {
         if (Object.prototype.hasOwnProperty.call(raw, 'config_rpt_valor')) return raw.config_rpt_valor;
         if (Object.prototype.hasOwnProperty.call(raw, 'config_valor')) return raw.config_valor;
         if (Object.prototype.hasOwnProperty.call(raw, 'valor')) return raw.valor;
         if (Object.prototype.hasOwnProperty.call(raw, 'value')) return raw.value;
         return raw;
       }
       return raw;
     };

     fieldsUniq.forEach((f, idx) => {
       const raw = datos && Object.prototype.hasOwnProperty.call(datos, f) ? datos[f] : undefined;
       const current = pickFieldVal(raw);
       const currentStr = current === undefined ? 'undefined' : String(current);
       const expStr = expectedUniq[idx] !== undefined ? String(expectedUniq[idx]) : '—';
       if (expStr !== '—') {
         lines.push(`${f} tiene un valor de ${currentStr}, se esperaba ${expStr}`);
       } else {
         lines.push(`${f} tiene un valor de ${currentStr}`);
       }
     });
    return lines.join('\n');
  } catch {
    return '';
  }
};

/**
 * Componente de menú desplegable para grupos y subgrupos
 * Muestra una estructura vertical con despliegue hacia abajo
 */
const MenuGruposSubgrupos = ({ 
  grupos = [], 
  onSeleccionGrupo = () => {}, 
  grupoSeleccionado = null,
  className = '',
  mostrarPreguntas = false,
  onRutaNavegacionChange = () => {}, // Nueva prop para comunicar cambios en la ruta
  datosFormulario = {}
}) => {
  const [elementosExpandidos, setElementosExpandidos] = useState(new Set());
  const [elementoSeleccionado, setElementoSeleccionado] = useState(null);
  const [historialNavegacion, setHistorialNavegacion] = useState([]);

  // Configuración de comportamiento de clic unificado tomada de MENU_CONFIG.navigation.clickBehavior
  const clickBehavior = (MENU_CONFIG && MENU_CONFIG.navigation && MENU_CONFIG.navigation.clickBehavior) || {
    unifyClickArea: true,
    primaryClickTarget: 'row',
    expandOnClickWhenHasChildren: true,
    selectLeafOnClick: true
  };

  // Username y lógica asociada eliminados para simplificar el menú

  // Notificar cambios en la ruta de navegación al componente padre
  useEffect(() => {
    onRutaNavegacionChange(historialNavegacion);
  }, [historialNavegacion, onRutaNavegacionChange]);

  // Inicializar grupos raíz
  useEffect(() => {
    if (grupos && grupos.length > 0) {
      setElementoSeleccionado(null);
      setElementosExpandidos(new Set());
      setHistorialNavegacion([]);
    }
  }, [grupos]);

  /**
   * Maneja la expansión/colapso de un elemento
   */
  const toggleExpansion = (elemento, uniqueKey) => {
    const nuevosExpandidos = new Set(elementosExpandidos);
    const elementoKey = uniqueKey || (elemento.id || `${elemento.tipo}_${elemento.titulo}`);
    if (nuevosExpandidos.has(elementoKey)) {
      nuevosExpandidos.delete(elementoKey);
    } else {
      nuevosExpandidos.add(elementoKey);
    }
    setElementosExpandidos(nuevosExpandidos);
  };

  // Helper reutilizable: verificar si el elemento tiene hijos del menú (gruposHijos o subgrupos)
  const hasMenuChildren = (elemento) => {
    return (
      Array.isArray(elemento.subgrupos) && elemento.subgrupos.length > 0
    ) || (
      Array.isArray(elemento.gruposHijos) && elemento.gruposHijos.length > 0
    );
  };

  /**
   * Maneja la selección de un elemento
   */
  const manejarSeleccion = (elemento, uniqueKey) => {
    const tieneHijos = hasMenuChildren(elemento);
    const tienePreguntasDirectas = Array.isArray(elemento.preguntas) && elemento.preguntas.length > 0;

    if (clickBehavior.expandOnClickWhenHasChildren && tieneHijos) {
      // Si tiene hijos, expandir/colapsar
      toggleExpansion(elemento, uniqueKey);
      // Si NO tiene preguntas directas, no seleccionar
      if (!tienePreguntasDirectas) return;
      // Si tiene preguntas directas, además seleccionar para mostrar contenido
    }

    // Seleccionar elemento cuando:
    // - Está permitido seleccionar hojas
    // - O bien el elemento tiene preguntas directas (aunque tenga hijos)
    if (clickBehavior.selectLeafOnClick || tienePreguntasDirectas) {
      setElementoSeleccionado(elemento);
      const nuevaRuta = construirRutaElemento(elemento);
      setHistorialNavegacion(nuevaRuta);
      onSeleccionGrupo(elemento, {
        ruta: nuevaRuta,
        hijos: obtenerHijosElemento(elemento)
      });
    }
  };

  /**
   * Construye la ruta completa hacia un elemento
   */
  const construirRutaElemento = (elemento) => {

    
    // Buscar la ruta completa desde la raíz hasta el elemento
    const ruta = [];
    
    // Función recursiva para encontrar el camino hacia el elemento
    const encontrarCamino = (elementos, elementoBuscado, caminoActual = []) => {
      for (const item of elementos) {
        const nuevoCamino = [...caminoActual, item];
        
        // Si encontramos el elemento, devolvemos el camino
        if (item.id === elementoBuscado.id) {
          return nuevoCamino;
        }
        
        // Buscar en grupos hijos
        if (item.gruposHijos && item.gruposHijos.length > 0) {
          const resultado = encontrarCamino(item.gruposHijos, elementoBuscado, nuevoCamino);
          if (resultado) return resultado;
        }
        
        // Buscar en subgrupos
        if (item.subgrupos && item.subgrupos.length > 0) {
          const resultado = encontrarCamino(item.subgrupos, elementoBuscado, nuevoCamino);
          if (resultado) return resultado;
        }
      }
      return null;
    };
    
    // Intentar encontrar el camino en los grupos disponibles
    const caminoEncontrado = encontrarCamino(grupos, elemento);
    
    if (caminoEncontrado) {
  
      return caminoEncontrado;
    } else {

      return [elemento];
    }
  };

  /**
   * Obtiene los elementos hijos de un elemento (grupos hijos, subgrupos, preguntas)
   */
  const obtenerHijosElemento = (elemento) => {
    const hijos = [];
    
    // Agregar grupos hijos
    if (elemento.gruposHijos && elemento.gruposHijos.length > 0) {
      hijos.push(...elemento.gruposHijos);
    }
    
    // Agregar subgrupos
    if (elemento.subgrupos && elemento.subgrupos.length > 0) {
      hijos.push(...elemento.subgrupos);
    }
    
    // Agregar preguntas si está habilitado
    if (mostrarPreguntas && elemento.preguntas && elemento.preguntas.length > 0) {
      hijos.push(...elemento.preguntas);
    }
    
    return hijos;
  };

  /**
   * Obtiene el icono apropiado para cada tipo de elemento
   */
  const obtenerIconoElemento = (elemento) => {
    switch (elemento.tipo) {
      case 'grupo':
        return '📁';
      case 'subgrupo':
        return '📂';
      case 'pregunta':
        return '❓';
      default:
        return '📄';
    }
  };

  /**
   * Obtiene la clase CSS para el estado de selección y filtrado por username
   */
  const obtenerClaseSeleccion = (elemento, estaExpandido) => {
    const esSeleccionado = elementoSeleccionado?.id === elemento.id;
    const esGrupoSeleccionadoGlobal = grupoSeleccionado?.id === elemento.id;
    
    let clases = 'form-master-elemento-item';
    
    if (esSeleccionado) {
      clases += ' form-master-selected';
    }
    
    if (esGrupoSeleccionadoGlobal) {
      clases += ' form-master-global-selected';
    }

    if (estaExpandido) {
      clases += ' form-master-expanded';
    }
    
    clases += ` form-master-tipo-${elemento.tipo}`;
    
    return clases;
  };

  /**
   * Renderiza un elemento del menú de forma recursiva
   */
  const renderizarElemento = (elemento, nivel = 0, parentPath = 'root', indexInParent = 0) => {
    // Verificar si el elemento debe filtrarse por nombre de grupo
    if (shouldFilterGroupByName(elemento)) {
      return null; // No renderizar el elemento si debe ser filtrado
    }

    // Evaluar visibilidad por relevant_transformed usando datos completos del formulario
    const tieneExpresion = elemento.relevant_transformed !== undefined && elemento.relevant_transformed !== null;
    const esVisiblePorRelevant = !tieneExpresion ? true : evaluateMenuRelevant(datosFormulario, elemento.relevant_transformed);

    // Depuración de evaluación de relevant eliminada para limpieza de código

    // Aplicar filtro por relevant según configuración
    if (MENU_CONFIG?.relevantFiltering?.applyRelevantFilters) {
      if (!esVisiblePorRelevant) {
        return null; // No renderizar si evaluateMenuRelevant indica que no es visible
      }
    }

    const tieneHijos = hasMenuChildren(elemento);
    const currentKeySegment = elemento.id || elemento.name || elemento.titulo || elemento.label || `${elemento.tipo}`;
    const elementKey = `${parentPath}/${currentKeySegment}#${indexInParent}`;
    const estaExpandido = elementosExpandidos.has(elementKey);
    const hijos = tieneHijos ? obtenerHijosElemento(elemento) : [];

    // Clases extra de evaluación cuando el filtro está desactivado y la visualización está activada
    let claseEvaluacion = '';
    if (!MENU_CONFIG?.relevantFiltering?.applyRelevantFilters && MENU_CONFIG?.relevantFiltering?.showEvaluationColors) {
      if (!tieneExpresion) {
        claseEvaluacion = 'relevant-eval-no-expression';
      } else if (esVisiblePorRelevant) {
        claseEvaluacion = 'relevant-eval-visible';
      } else {
        claseEvaluacion = 'relevant-eval-hidden';
      }
    }

    return (
      <div key={elementKey} className="form-master-elemento-container">
        <div
          className={`${obtenerClaseSeleccion(elemento, estaExpandido)} ${claseEvaluacion}`}
          style={{ paddingLeft: `${nivel * 20 + 12}px`, position: 'relative' }}
          onClick={() => manejarSeleccion(elemento, elementKey)}
        >
          <div className="form-master-item-content">
          {tieneHijos && (
            <span 
              className={`form-master-item-toggle ${estaExpandido ? 'form-master-expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                // Respetar configuración: unificar o solo expandir con la flecha
                if (clickBehavior.unifyClickArea) {
                  manejarSeleccion(elemento, elementKey);
                } else {
                  toggleExpansion(elemento, elementKey);
                }
              }}
            >
              {estaExpandido ? '▼' : '▶'}
            </span>
          )}
            <span className="form-master-item-icon">
              {obtenerIconoElemento(elemento)}
            </span>
            <span 
              className={`form-master-item-titulo`}
              data-relevant={
                !tieneExpresion ? 'no-expression' : (esVisiblePorRelevant ? 'visible' : 'hidden')
              }
            >
              {obtenerTituloElemento(elemento)}
              {MENU_CONFIG?.relevantFiltering?.showDebugConditionBadge && (
                (() => {
                  const estadoRelevante = !tieneExpresion ? 'no-expression' : (esVisiblePorRelevant ? 'visible' : 'hidden');
                  const exprDesc = describeRelevantExpression(elemento.relevant_transformed);
                  const maxLen = MENU_CONFIG?.relevantFiltering?.maxExpressionLength ?? 140;
                  const tooltip = MENU_CONFIG?.relevantFiltering?.showRawRelevantExpression
                    ? (() => { try { return JSON.stringify(elemento.relevant_transformed); } catch { return exprDesc; } })()
                    : (MENU_CONFIG?.relevantFiltering?.showDebugValues
                        ? buildDebugValuesTooltip(elemento.relevant_transformed, datosFormulario)
                        : exprDesc);
                  const exprText = MENU_CONFIG?.relevantFiltering?.showDebugValues
                    ? (() => { const v = buildDebugValuesTooltip(elemento.relevant_transformed, datosFormulario); return (v && v.split('\n')[0]) || exprDesc; })()
                    : exprDesc;
                  const exprShown = exprText && exprText.length > maxLen ? `${exprText.slice(0, maxLen)}…` : exprText;
                  return (
                    <span className={`form-master-relevant-debug-badge ${estadoRelevante}`} title={tooltip} onClick={(e) => e.stopPropagation()}>
                      <span className="state-dot" />
                      <span className="expr">{exprShown || (tieneExpresion ? '(expresión)' : '—')}</span>
                    </span>
                  );
                })()
              )}
            </span>
          </div>
        </div>
        
        {estaExpandido && tieneHijos && (
          <div className="form-master-hijos-container">
            {hijos.map((hijo, idx) => renderizarElemento(hijo, nivel + 1, elementKey, idx))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Renderiza la ruta de navegación (breadcrumb)
   */
  const renderizarRutaNavegacion = () => {
    
    
    if (historialNavegacion.length === 0) {
      return null;
    }

    return (
      <div className="form-master-ruta-navegacion">
        <span className="form-master-ruta-label">Ruta:</span>
        {historialNavegacion.map((elemento, index) => {
          
          const titulo = obtenerTituloElemento(elemento);
          
          
          return (
            <React.Fragment key={elemento.id || index}>
              <span className="form-master-ruta-elemento">
                {obtenerIconoElemento(elemento)} {titulo}
              </span>
              {index < historialNavegacion.length - 1 && (
                <span className="form-master-ruta-separador"> → </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`form-master-menu-grupos-subgrupos hover-disabled ${className}`}>
      {/* Contenedor principal del menú */}
      <div className="form-master-menu-container">
        {grupos && grupos.length > 0 ? (
          grupos
            .filter(g => g.tipo === 'grupo' || g.tipo === 'subgrupo')
            .map((grupo, idx) => renderizarElemento(grupo, 0, 'root', idx))
        ) : (
          <div className="form-master-mensaje-vacio">
            No hay grupos disponibles
          </div>
        )}
      </div>

    </div>
  );
};

export default MenuGruposSubgrupos;